import { useEffect, useState } from "react";
import { Copy, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../panel/PanelShell";
import { Field, inputCls } from "../forms/Fields";
import { api, formatApiError, API } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";

const STEPS = [
  "Open Google Search Console (search.google.com/search-console) and add quantora.co.in as a property.",
  "Verify ownership. The Domain (DNS TXT record) method is the most reliable. Alternatively, choose the HTML tag method and paste only the content value below.",
  "Go to Sitemaps and submit the sitemap URL shown on this page.",
  "Use URL Inspection → Request indexing for the homepage and key pages to speed up discovery.",
  "Repeat with Bing Webmaster Tools (it can import directly from Search Console).",
];

function CopyRow({ label, value, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
      <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <code className="flex-1 truncate text-sm text-navy" data-testid={testid}>{value}</code>
      <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} data-testid={`${testid}-copy`} className="text-slate-400 hover:text-navy" aria-label="Copy"><Copy size={15} /></button>
    </div>
  );
}

export default function SeoAdmin() {
  const { reload } = useSettings();
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = () => Promise.all([api.get("/seo/summary"), api.get("/admin/settings")]).then(([a, b]) => { setSummary(a.data); setSettings(b.data); });
  useEffect(() => { load(); }, []);
  if (!summary || !settings) return null;

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", settings);
      await reload();
      await load();
      toast.success("Search settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value.trim() });

  return (
    <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
      <div className="space-y-6">
        <Card className="space-y-3" data-testid="seo-urls-card">
          <h3 className="mb-2 flex items-center gap-2 font-bold text-navy"><Search size={17} className="text-mint-600" /> Search engine endpoints</h3>
          <CopyRow label="Sitemap" value={summary.sitemap_url} testid="seo-sitemap-url" />
          <CopyRow label="Robots" value={summary.robots_url} testid="seo-robots-url" />
          <div className="flex items-center justify-between pt-2 text-sm">
            <span className="text-slate-600"><b className="font-num text-navy" data-testid="seo-url-count">{summary.url_count}</b> pages in sitemap (updates automatically)</span>
            <a href={`${API}/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="seo-view-sitemap" className="inline-flex items-center gap-1 font-semibold text-navy hover:text-mint-600">View live sitemap <ExternalLink size={14} /></a>
          </div>
        </Card>
        <Card>
          <h3 className="mb-5 font-bold text-navy">Verification & domain</h3>
          <form onSubmit={save} className="grid gap-4" data-testid="seo-form">
            <Field label="Primary site URL (used in sitemap & canonical links)" id="seo-site"><input id="seo-site" required className={inputCls} value={settings.site_url || ""} onChange={set("site_url")} data-testid="seo-site-url-input" /></Field>
            <Field label="Google Search Console verification code (content value)" id="seo-google"><input id="seo-google" className={inputCls} value={settings.google_site_verification || ""} onChange={set("google_site_verification")} placeholder="e.g. abc123XYZ…" data-testid="seo-google-verification-input" /></Field>
            <Field label="Bing Webmaster verification code" id="seo-bing"><input id="seo-bing" className={inputCls} value={settings.bing_site_verification || ""} onChange={set("bing_site_verification")} data-testid="seo-bing-verification-input" /></Field>
            <Field label="Google Analytics Measurement ID" id="seo-ga"><input id="seo-ga" className={inputCls} value={settings.ga_measurement_id || ""} onChange={set("ga_measurement_id")} placeholder="G-XXXXXXX" data-testid="seo-ga-input" /></Field>
            <button data-testid="seo-save-btn" className="w-fit rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white">Save</button>
          </form>
        </Card>
      </div>
      <div className="space-y-6">
        <Card>
          <h3 className="mb-4 font-bold text-navy">Get indexed on Google</h3>
          <ol className="space-y-3">
            {STEPS.map((s, i) => <li key={i} className="flex gap-3 text-sm text-slate-600"><span className="font-num text-gold-600">{i + 1}.</span>{s}</li>)}
          </ol>
        </Card>
        <Card className="max-h-96 overflow-y-auto">
          <h3 className="mb-3 font-bold text-navy">Indexed URLs</h3>
          <ul className="space-y-1.5 text-xs text-slate-600" data-testid="seo-url-list">{summary.urls.map((u) => <li key={u} className="truncate">{u}</li>)}</ul>
        </Card>
      </div>
    </div>
  );
}
